import {
  KentFeature,
  KentFeatureDto,
  KentUnspecifiedFeature,
  KentFeatureBase,
  KentFeatureStatus,
  KentFeatureStatusDto,
  KentFeatureConfig,
  KentFeatureConfigDto,
  KentUnspecifiedFeatureConfig,
  KentFeatureMetricsStatus,
  KentFeatureMetricsStatusDto,
  KentClusterOverviewDto,
  KentClusterOverview,
} from '@cast/types-kent';

const mapKentFeatureStatusDtoToKentFeatureStatus = (
  status: KentFeatureStatusDto
): KentFeatureStatus => {
  switch (status) {
    case 'FEATURE_STATUS_UNSPECIFIED':
      return 'FEATURE_STATUS_UNSPECIFIED';
    case 'ENABLED':
      return 'ENABLED';
    case 'DISABLED':
      return 'DISABLED';
    case 'UNAVAILABLE':
      return 'UNAVAILABLE';
    default:
      return 'FEATURE_STATUS_UNSPECIFIED';
  }
};

const mapKentFeatureMetricsStatusDtoToKentFeatureMetricsStatus = (
  status?: KentFeatureMetricsStatusDto
): KentFeatureMetricsStatus => {
  switch (status) {
    case 'READY':
    case 'GENERATING':
    case 'ERROR':
      return status;
    default:
      return 'UNKNOWN';
  }
};
const unspecifiedFeature: KentUnspecifiedFeature = {
  type: 'FEATURE_TYPE_UNSPECIFIED',
};

export const mapKentFeatureDtoToKentFeature = (
  dto: KentFeatureDto
): KentFeature => {
  const { id, status, type } = dto;
  const featureBase: KentFeatureBase = {
    id,
    status: mapKentFeatureStatusDtoToKentFeatureStatus(
      status || 'FEATURE_STATUS_UNSPECIFIED'
    ),
  };

  switch (type) {
    case 'CLM':
      return {
        ...featureBase,
        type: 'CLM',
      };

    case 'POD_MUTATIONS':
      if (!dto.podMutations) {
        return unspecifiedFeature;
      }
      return {
        ...featureBase,
        type: 'POD_MUTATIONS',
        podMutations: dto.podMutations,
      };

    case 'REBALANCING':
      if (!dto.rebalancing) {
        return unspecifiedFeature;
      }
      return {
        ...featureBase,
        type: 'REBALANCING',
        rebalancing: dto.rebalancing,
        metricsStatus: mapKentFeatureMetricsStatusDtoToKentFeatureMetricsStatus(
          dto.metricsStatus
        ),
        metricsTime: dto.metricsTime,
      };

    case 'WORKLOAD_RIGHT_SIZING':
      if (!dto.workloadRightSizing) {
        return unspecifiedFeature;
      }
      return {
        ...featureBase,
        type: 'WORKLOAD_RIGHT_SIZING',
        workloadRightSizing: dto.workloadRightSizing,
      };

    case 'SPOT_INTERRUPTION_PREDICTION':
      if (!dto.spotInterruptionPrediction) {
        return unspecifiedFeature;
      }
      return {
        ...featureBase,
        type: 'SPOT_INTERRUPTION_PREDICTION',
        spotInterruptionPrediction: dto.spotInterruptionPrediction,
      };

    case 'CONTINUOUS_REBALANCING':
      if (!dto.continuousRebalancing) {
        return unspecifiedFeature;
      }
      return {
        ...featureBase,
        type: 'CONTINUOUS_REBALANCING',
        continuousRebalancing: dto.continuousRebalancing,
      };

    default:
      return unspecifiedFeature;
  }
};

const unspecifiedFeatureConfig: KentUnspecifiedFeatureConfig = {
  type: 'FEATURE_TYPE_UNSPECIFIED',
};

export const mapKentFeatureConfigDtoToKentFeatureConfig = (
  dto: KentFeatureConfigDto
): KentFeatureConfig => {
  const { id, status, type } = dto;
  const featureBase: KentFeatureBase = {
    id,
    status: mapKentFeatureStatusDtoToKentFeatureStatus(
      status || 'FEATURE_STATUS_UNSPECIFIED'
    ),
  };

  switch (type) {
    case 'CLM':
    case 'POD_MUTATIONS':
    case 'REBALANCING':
    case 'WORKLOAD_RIGHT_SIZING':
    case 'SPOT_INTERRUPTION_PREDICTION':
      return {
        ...featureBase,
        type,
      };
    case 'CONTINUOUS_REBALANCING':
      return {
        ...featureBase,
        type,
        config: dto.continuousRebalancingConfig,
      };

    default:
      return unspecifiedFeatureConfig;
  }
};

export const mapKentOverviewDtoToKentOverview = (
  dto: KentClusterOverviewDto
): KentClusterOverview => {
  return {
    summary: dto.summary || {},
    metricsStatus: mapKentFeatureMetricsStatusDtoToKentFeatureMetricsStatus(
      dto.metricsStatus
    ),
    features: (dto.features || []).map(mapKentFeatureDtoToKentFeature),
  };
};
